import { normalizeName } from "../parser/item-input.js";
import type { ChainKey, CategoryKey } from "./generated.js";
import type { List, Store } from "../types/entities.js";
import { ohneAkzente } from "./text.js";

/**
 * In welchen Laden gehört das, wenn es nicht der Supermarkt ist.
 *
 * Die dritte Frage des Grundwissens, neben der Warengruppe (welcher Gang) und
 * der Grundzutat (überhaupt auf den Zettel). Hier geht es um den Laden, und
 * damit um die Liste: Die Familie führt je eine für Supermarkt, Asiamarkt,
 * Drogerie und Baumarkt.
 *
 * Daniel am 03.10.2026: "Es gibt einige Dinge, die tendenziell eher im
 * Asiamarkt zu finden sind. Das heißt nicht, dass man die nicht auch woanders
 * bekommt, aber letztendlich sollten alle asiatischen Dinge in die
 * Einkaufsliste Asiamarkt kommen, wenn es eine passende Einkaufsliste gibt."
 *
 * Das ist die Linie: Nicht "wo gibt es das ausschließlich", sondern "wo kauft
 * diese Familie es". Sojasauce steht in jedem Supermarkt und trotzdem hier.
 *
 * Null heißt Supermarkt. Es ist der Normalfall und braucht keine Tabelle.
 */

export const SHOP_KEYS = ["asiamarkt", "baumarkt", "drogerie"] as const;
export type ShopKey = (typeof SHOP_KEYS)[number];

/**
 * Asiatisch, gemessen an den Rezepten der Familie.
 *
 * Gezählt über die 42 Rezept-PDFs: Sojasauce 42 Treffer, Reisessig 15,
 * Fischsauce 14, dann Teriyaki, Sriracha, Kokosmilch, Currypaste, Jasminreis,
 * Ramen, Glasnudeln, Sambal, Panko, Pak Choi, Mirin, Kimchi, Gochujang,
 * Gochugaru, Edamame, Zitronengras. Dazu, was am 03.10.2026 in der
 * Einkaufsliste der Spaisekammer lag: Toban Djan und Tsuyu.
 *
 * Bewusst **nicht** hier: Reis, Nudeln, Ingwer, Knoblauch, Curry, Kurkuma und
 * Garam Masala. Die stehen im deutschen Supermarkt im Regal, und wer sie in
 * den Asiamarkt schickt, schickt jemanden für eine Tüte Reis quer durch die
 * Stadt.
 */
const ASIAMARKT: readonly string[] = [
  // Saucen und Pasten
  "sojasauce", "sojasoße", "sojasosse", "fischsauce", "austernsauce", "hoisin", "teriyaki",
  "sriracha", "sambal", "sambal oelek", "gochujang", "gochugaru", "toban djan", "tobandjan",
  "doubanjiang", "ponzu", "tsuyu", "mentsuyu", "tamari", "shoyu", "ketjap", "kecap",
  "currypaste", "tom yum", "tom kha", "chiliöl", "chili crisp", "sweet chili", "xo sauce",
  // Grundzutaten der Küche
  "mirin", "sake", "reiswein", "shaoxing", "reisessig", "sesamöl", "miso", "dashi",
  "sichuanpfeffer", "szechuanpfeffer", "fünf gewürze", "funf gewurze",
  // Trockenes
  "nori", "wakame", "kombu", "udon", "soba", "ramennudeln", "reisnudeln", "glasnudeln",
  "reispapier", "wonton", "panko", "pankomehl", "tapiokastärke", "tapioka",
  "jasminreis", "sushireis", "klebreis",
  // Frisches und Gekühltes
  "tofu", "seidentofu", "tempeh", "kimchi", "edamame", "pak choi", "bok choy", "sojasprossen",
  "zitronengras", "galgant", "thai basilikum", "thaibasilikum", "limettenblatt", "limettenblätter",
  "shiitake", "enoki", "kräuterseitling", "daikon", "mochi", "gyoza", "dumplings", "frühlingsrolle",
];

/**
 * Baumarkt.
 *
 * Daniels Beispiel vom 03.10.2026: "Wenn ich Schrauben eingebe, sollten die
 * Schrauben automatisch in der Baumarktliste sein." Eng gehalten: Was es an
 * der Supermarktkasse auch gibt (Klebeband, Batterien, Müllbeutel), bleibt
 * beim Supermarkt.
 */
const BAUMARKT: readonly string[] = [
  "schraube", "schrauben", "dübel", "duebel", "nagel", "nägel", "mutter", "unterlegscheibe",
  "winkel", "scharnier", "bohrer", "säge", "sägeblatt", "schleifpapier", "schmirgelpapier",
  "silikon", "acryl", "bauschaum", "montagekleber", "holzleim", "spachtelmasse",
  "wandfarbe", "lack", "lasur", "grundierung", "pinsel", "farbrolle", "malerkrepp", "abdeckfolie",
  "fliese", "fuge", "fugenmasse", "mörtel", "zement", "gips",
  "kabelbinder", "dichtung", "dichtungsring", "schlauchschelle", "wasserhahn", "siphon",
  "leuchtmittel", "glühbirne", "led-lampe", "verlängerungskabel", "steckdose", "lichtschalter",
  "blumenerde", "pflanzerde", "dünger", "rindenmulch", "pflanzkübel", "gießkanne",
  "werkzeug", "zange", "hammer", "schraubendreher", "akkuschrauber", "zollstock", "wasserwaage",
];

/**
 * Wörter, die einen Treffer aufheben.
 *
 * "Mutter" ist meistens ein Mensch, "Nagel" kann zur Pflege gehören, und eine
 * Sojasauce im Namen eines Fertiggerichts macht dieses nicht zur Asiaware.
 */
const KEIN_LADEN: readonly string[] = [
  "muttertag", "nagellack", "nagelfeile", "fingernagel", "nagelschere",
  "sojamilch", "sojajoghurt", "sojadrink", "sojaschnetzel",
  "tofuwurst", "farbstift", "farbkasten", "wasserfarbe",
];

/** Woran die Warengruppe allein schon den Laden verrät. */
const LADEN_JE_WARENGRUPPE: Partial<Record<CategoryKey, ShopKey>> = {
  drogerie: "drogerie",
};

/**
 * Wo lohnt die Rückfrage, wenn die Liste fehlt?
 *
 * Nur dort, wo der Supermarkt die Ware wirklich nicht führt. Schrauben gibt
 * es dort nicht, Zahnpasta und Sojasauce schon. Wer "Zahnpasta" eintippt und
 * keine Drogerie-Liste hat, bekommt deshalb kein Blatt, sondern seine Zeile:
 * Eine Frage, die man mit "ist doch egal" beantwortet, hätte man nicht
 * stellen sollen.
 *
 * Am 03.10.2026 nachgeschärft, nachdem acht E2E-Tests im CI über genau diesen
 * Fall gestolpert sind. Sie tippen Zahnpasta und Shampoo ein und warteten
 * danach vergeblich auf ihre Zeile.
 */
const FRAGT_NACH_LISTE: readonly ShopKey[] = ["baumarkt"];

/** Soll die App nachfragen, wenn es für diesen Laden keine Liste gibt? */
export function fragtNachListe(shop: ShopKey): boolean {
  return FRAGT_NACH_LISTE.includes(shop);
}

export interface ShopFrage {
  name: string;
  /** Die Warengruppe, falls bekannt. Sie entscheidet bei der Drogerie. */
  category?: CategoryKey | null;
}

/**
 * Welcher Laden, wenn nicht der Supermarkt?
 *
 * Erst der Name, dann die Warengruppe: Ein Name ist genauer als eine Gruppe.
 * "Zahnpasta" ist Drogerie über beide Wege, "Sojasauce" nur über den Namen,
 * denn ihre Warengruppe ist Trockensortiment wie bei Mehl.
 */
export function guessShop(frage: ShopFrage): ShopKey | null {
  const voll = ohneAkzente(normalizeName(frage.name));
  if (voll.length === 0) return null;

  if (KEIN_LADEN.some((wort) => voll.includes(ohneAkzente(wort)))) return null;

  // Teilwort genügt hier, anders als bei den Grundzutaten: "Sojasaucenflasche"
  // und "Bio-Sojasauce" sind beide Sojasauce, und ein falscher Treffer kostet
  // nur eine Zeile in der falschen Liste, nicht eine vergessene Zutat.
  if (ASIAMARKT.some((wort) => voll.includes(ohneAkzente(wort)))) return "asiamarkt";
  if (BAUMARKT.some((wort) => voll.includes(ohneAkzente(wort)))) return "baumarkt";

  return frage.category ? (LADEN_JE_WARENGRUPPE[frage.category] ?? null) : null;
}

/**
 * Welche Ketten zu einem Ladentyp gehören.
 *
 * Nur für den Fall, dass eine Liste mit einem Laden verknüpft ist. Die
 * Familie hat das bisher nur beim Supermarkt gemacht (ALDI Süd), aber wer
 * einen Asiamarkt als Laden anlegt, soll ihn hier wiederfinden.
 */
const KETTEN_JE_LADEN: Record<ShopKey, readonly ChainKey[]> = {
  asiamarkt: ["asiamarkt"],
  baumarkt: ["baumarkt"],
  drogerie: ["dm", "rossmann"],
};

/** Wonach eine Liste heißt, die zu diesem Laden gehört. */
const NAMEN_JE_LADEN: Record<ShopKey, readonly string[]> = {
  asiamarkt: ["asiamarkt", "asia", "asialaden", "asiashop"],
  baumarkt: ["baumarkt", "obi", "bauhaus", "hornbach", "toom"],
  drogerie: ["drogerie", "dm", "rossmann", "mueller", "müller"],
};

/**
 * Die Liste, die zu diesem Laden gehört, oder null.
 *
 * Zwei Wege, in dieser Reihenfolge: Erst die Verknüpfung über den Laden, weil
 * sie ausdrücklich gesetzt wurde; dann der Name, weil die Familie ihre Listen
 * nach Läden benennt, ohne sie zu verknüpfen. "Asiamarkt" findet so die
 * richtige Liste, auch wenn dort kein Laden hinterlegt ist.
 *
 * Gelöschte Listen zählen nicht. Gibt es mehrere Treffer, gewinnt die mit dem
 * kleinsten sort_index, also die, die in der App oben steht.
 */
export function listeFuerLaden(shop: ShopKey | null, listen: readonly List[], laeden: readonly Store[] = []): List | null {
  if (shop === null) return null;

  const offen = listen.filter((liste) => liste.deleted_at === null).sort((a, b) => a.sort_index - b.sort_index);
  const ketten = KETTEN_JE_LADEN[shop];

  const ueberLaden = offen.find((liste) => {
    if (liste.store_id === null) return false;
    const laden = laeden.find((eintrag) => eintrag.id === liste.store_id && eintrag.deleted_at === null);
    return laden?.chain != null && ketten.includes(laden.chain);
  });
  if (ueberLaden) return ueberLaden;

  const namen = NAMEN_JE_LADEN[shop];
  return offen.find((liste) => namen.some((wort) => ohneAkzente(normalizeName(liste.name)).includes(wort))) ?? null;
}
